/**
 * WorkflowEditor - Admin page for configuring workflows
 *
 * Structure:
 * - Estados tab: Per-workflow status configuration
 * - Transiciones tab: Per-workflow type transition configuration
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Loader2,
  Palette,
  Settings2,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { pageStyles } from '../../../lib/styles';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import type { UnifiedCaseType, WorkflowStatus, WorkflowTransition } from '@leal-crm/shared';
import { BOOKING_TYPE_INFO, BOOKING_TYPES } from './constants';
import { useResetWorkflows, useWorkflowEditorData, useWorkflowEditorMutations } from './hooks';
import { TransitionRow } from './components/TransitionRow';
import { StatusCreateDialog } from './components/dialogs/StatusCreateDialog';
import { StatusEditDialog } from './components/dialogs/StatusEditDialog';
import { TransitionEditDialog } from './components/dialogs/TransitionEditDialog';

// ============================================================================
// Main Component
// ============================================================================

export default function WorkflowEditorPage() {
  const [activeTab, setActiveTab] = useState<'statuses' | 'transitions'>('statuses');
  const [workflowType, setWorkflowType] = useState<'booking' | 'job'>('booking');
  const [selectedType, setSelectedType] = useState<UnifiedCaseType>('private');
  const [editingStatus, setEditingStatus] = useState<WorkflowStatus | null>(null);
  const [editingTransition, setEditingTransition] = useState<WorkflowTransition | null>(null);
  const [statusSearch, setStatusSearch] = useState('');
  const [newTransitionFromId, setNewTransitionFromId] = useState<string | undefined>();
  const [newTransitionToId, setNewTransitionToId] = useState<string | undefined>();
  const [isCreateStatusOpen, setIsCreateStatusOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowStatus | null>(null);
  const [deleteWithFallback, setDeleteWithFallback] = useState(false);

  const { success: showSuccess, error: showError } = useToast();
  const {
    workflows,
    presets,
    preconditionDefs,
    postactionDefs,
    emailTemplates,
    currentWorkflow,
    checklistItems,
    isLoading,
    isError,
    error,
  } = useWorkflowEditorData(workflowType, selectedType);

  const resetWorkflowsMutation = useResetWorkflows({
    onSuccess: (message) => showSuccess(message),
    onError: (message) => showError(message),
  });

  const {
    cloneWorkflowMutation,
    createStatusMutation,
    updateStatusMutation,
    deleteStatusMutation,
    updateTransitionMutation,
    createTransitionMutation,
    createChecklistItemMutation,
    updateChecklistItemMutation,
    deleteChecklistItemMutation,
  } = useWorkflowEditorMutations({
    workflowId: currentWorkflow?.workflow.id,
    onSuccess: (message) => showSuccess(message),
    onError: (message) => showError(message),
    onDeleteError: (err) => {
      const details = err.details as Record<string, unknown>;
      const activeBookings = Number(details.activeBookings ?? 0);
      const activeJobs = Number(details.activeJobs ?? 0);
      if (activeBookings || activeJobs) {
        showError(`Estado en uso: ${activeBookings} citas activas, ${activeJobs} trabajos activos`);
        return;
      }
      showError(err instanceof Error ? err.message : 'No se pudo eliminar el estado');
    },
  });

  useEffect(() => {
    setNewTransitionFromId(undefined);
    setNewTransitionToId(undefined);
  }, [selectedType, workflowType]);

  const filteredStatuses = useMemo(() => {
    if (!currentWorkflow?.statuses) return [];
    const term = statusSearch.trim().toLowerCase();
    return currentWorkflow.statuses
      .filter((status) => {
        if (!term) return true;
        return status.name.toLowerCase().includes(term) || status.code.toLowerCase().includes(term);
      })
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [currentWorkflow, statusSearch]);

  const moveStatus = useCallback(
    async (status: WorkflowStatus, direction: 'up' | 'down') => {
      if (!currentWorkflow) return;
      const ordered = currentWorkflow.statuses.slice().sort((a, b) => a.sortOrder - b.sortOrder);
      const index = ordered.findIndex((s) => s.id === status.id);
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (index === -1 || targetIndex < 0 || targetIndex >= ordered.length) return;

      const target = ordered[targetIndex];
      const currentOrder = status.sortOrder;
      const targetOrder = target.sortOrder;

      await updateStatusMutation.mutateAsync({
        workflowId: currentWorkflow.workflow.id,
        statusId: status.id,
        updates: { sortOrder: targetOrder },
      });
      await updateStatusMutation.mutateAsync({
        workflowId: currentWorkflow.workflow.id,
        statusId: target.id,
        updates: { sortOrder: currentOrder },
      });
    },
    [currentWorkflow, updateStatusMutation]
  );


  // Helper: get status by ID
  const getStatusById = useCallback(
    (statusId: string) => currentWorkflow?.statuses.find((s) => s.id === statusId),
    [currentWorkflow]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-96 mt-2" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="font-medium">Error al cargar flujos de trabajo</p>
                <p className="text-sm">{error instanceof Error ? error.message : 'Error desconocido'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageStyles.title}>Flujos de Trabajo</h1>
          <p className={pageStyles.subtitle}>
            Configura los estados y transiciones del sistema
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={resetWorkflowsMutation.isPending}
            onClick={async () => {
              const presetId = presets[0]?.id || 'default-v2';
              const label = presets.find((p) => p.id === presetId)?.name || presetId;
              const ok = confirm(
                `Restablecer TODOS los flujos a "${label}"?\n\nEsto sobrescribe estados, transiciones, colores, constraints y checklist items. Esta acción no se puede deshacer (salvo otro reset).`
              );
              if (!ok) return;
              await resetWorkflowsMutation.mutateAsync({ presetId });
            }}
          >
            {resetWorkflowsMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset a default
          </Button>
        </div>
      </div>

      {/* Workflow Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant={workflowType === 'booking' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWorkflowType('booking')}
              >
                Citas
              </Button>
              <Button
                variant={workflowType === 'job' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWorkflowType('job')}
              >
                Trabajos
              </Button>
            </div>

            {(workflowType === 'booking' || workflowType === 'job') && (
              <div className="flex flex-wrap items-center gap-2">
                {BOOKING_TYPES.map((type) => {
                  const info = BOOKING_TYPE_INFO[type];
                  const isActive = selectedType === type;
                  const Icon = info.icon;
                  return (
                    <Button
                      key={type}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedType(type)}
                      className="gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      {info.name}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'statuses' | 'transitions')}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="statuses" className="gap-2">
            <Palette className="h-4 w-4" />
            Estados
          </TabsTrigger>
          <TabsTrigger value="transitions" className="gap-2">
            <ArrowRight className="h-4 w-4" />
            Transiciones
          </TabsTrigger>
        </TabsList>

        {/* ================================================================ */}
        {/* ESTADOS TAB - Global configuration */}
        {/* ================================================================ */}
        <TabsContent value="statuses" className="mt-6">
          {currentWorkflow ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Estados del Flujo</CardTitle>
                <CardDescription>
                  {workflowType === 'booking'
                    ? `Citas · ${BOOKING_TYPE_INFO[selectedType].name}`
                    : `Trabajos · ${BOOKING_TYPE_INFO[selectedType].name}`}
                  {currentWorkflow?.workflow.createdAt && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      · ID: {currentWorkflow.workflow.id.slice(0, 8)}
                      · Creado: {new Date(currentWorkflow.workflow.createdAt).toLocaleDateString('es-ES')}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="w-full md:max-w-xs">
                    <Label htmlFor="status-search" className="text-xs text-muted-foreground">
                      Buscar estado
                    </Label>
                    <Input
                      id="status-search"
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      placeholder="Nombre o código"
                    />
                  </div>
                  <Button size="sm" onClick={() => setIsCreateStatusOpen(true)}>
                    Nuevo estado
                  </Button>
                </div>

                <div className="-mx-3 mt-4 overflow-x-auto px-3 sm:mx-0 sm:px-0">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 font-medium">Estado</th>
                        <th className="text-left p-3 font-medium">Código</th>
                        <th className="text-center p-3 font-medium w-24">Tipo</th>
                        <th className="text-center p-3 font-medium w-20">Orden</th>
                        {workflowType === 'job' && (
                          <th className="text-center p-3 font-medium w-24">Tablero</th>
                        )}
                        {workflowType === 'booking' && (
                          <th className="text-center p-3 font-medium w-24">Cliente</th>
                        )}
                        <th className="text-center p-3 font-medium w-24">Activo</th>
                        <th className="text-right p-3 font-medium w-28">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStatuses.map((status) => (
                        <tr key={status.id} className="border-b hover:bg-muted/20">
                          <td className="p-3">
                            <span
                              className="px-2.5 py-1 rounded-md text-xs font-medium border"
                              style={{
                                color: status.color,
                                backgroundColor: status.bgColor,
                                borderColor: status.borderColor,
                              }}
                            >
                              {status.name}
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px] font-mono">
                              {status.code}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px]',
                                status.statusType === 'start' && 'border-green-500 text-green-700',
                                status.statusType === 'terminal' && 'border-red-500 text-red-700'
                              )}
                            >
                              {status.statusType === 'start'
                                ? 'Inicio'
                                : status.statusType === 'terminal'
                                ? 'Final'
                                : 'Normal'}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <div className="inline-flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => moveStatus(status, 'up')}
                                disabled={updateStatusMutation.isPending}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => moveStatus(status, 'down')}
                                disabled={updateStatusMutation.isPending}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                          {workflowType === 'job' && (
                            <td className="p-3 text-center">
                              <Switch
                                checked={status.showInJobboard}
                                onCheckedChange={(checked) =>
                                  updateStatusMutation.mutate({
                                    workflowId: currentWorkflow.workflow.id,
                                    statusId: status.id,
                                    updates: { showInJobboard: checked },
                                  })
                                }
                                disabled={updateStatusMutation.isPending}
                              />
                            </td>
                          )}
                          {workflowType === 'booking' && (
                            <td className="p-3 text-center">
                              <Switch
                                checked={status.isVisibleToCustomer}
                                onCheckedChange={(checked) =>
                                  updateStatusMutation.mutate({
                                    workflowId: currentWorkflow.workflow.id,
                                    statusId: status.id,
                                    updates: { isVisibleToCustomer: checked },
                                  })
                                }
                                disabled={updateStatusMutation.isPending}
                              />
                            </td>
                          )}
                          <td className="p-3 text-center">
                            <Switch
                              checked={status.isActive}
                              onCheckedChange={(checked) =>
                                updateStatusMutation.mutate({
                                  workflowId: currentWorkflow.workflow.id,
                                  statusId: status.id,
                                  updates: { isActive: checked },
                                })
                              }
                              disabled={updateStatusMutation.isPending}
                            />
                          </td>
                          <td className="p-3 text-right">
                            <div className="inline-flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingStatus(status)}
                              >
                                <Settings2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget(status)}
                                disabled={status.code === 'UNDEFINED'}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredStatuses.length === 0 && (
                        <tr>
                          <td colSpan={workflowType === 'job' ? 7 : 7} className="p-6 text-center text-muted-foreground">
                            No hay estados configurados
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-muted-foreground">
                  No hay flujo configurado para {workflowType === 'booking'
                    ? BOOKING_TYPE_INFO[selectedType].name
                    : `Trabajos · ${BOOKING_TYPE_INFO[selectedType].name}`}
                </div>
                {workflowType === 'job' && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      size="sm"
                      onClick={() => {
                        const baseWorkflow = workflows.find(
                          (w) => w.workflow.workflowType === 'job' && w.workflow.bookingType === null
                        ) || workflows.find((w) => w.workflow.workflowType === 'job');
                        if (!baseWorkflow) {
                          showError('No hay un flujo base de trabajos');
                          return;
                        }
                        cloneWorkflowMutation.mutate({
                          sourceWorkflowId: baseWorkflow.workflow.id,
                          bookingType: selectedType,
                        });
                      }}
                      disabled={cloneWorkflowMutation.isPending}
                    >
                      {cloneWorkflowMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      Crear flujo de trabajos
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* TRANSICIONES TAB - Per workflow type */}
        {/* ================================================================ */}
        <TabsContent value="transitions" className="mt-6 space-y-4">
          {currentWorkflow ? (
            <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Crear transición</CardTitle>
                    <CardDescription>
                      Elige origen y destino. Se crea con diálogo "Ninguno" y puedes editarla después.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
                      <div className="space-y-2">
                        <Label>Desde</Label>
                        <Select
                          value={newTransitionFromId}
                          onValueChange={setNewTransitionFromId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona estado" />
                          </SelectTrigger>
                          <SelectContent>
                            {currentWorkflow.statuses
                              .slice()
                              .sort((a, b) => a.sortOrder - b.sortOrder)
                              .map((status) => (
                                <SelectItem key={status.id} value={status.id}>
                                  {status.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Hacia</Label>
                        <Select
                          value={newTransitionToId}
                          onValueChange={setNewTransitionToId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona estado" />
                          </SelectTrigger>
                          <SelectContent>
                            {currentWorkflow.statuses
                              .slice()
                              .sort((a, b) => a.sortOrder - b.sortOrder)
                              .map((status) => (
                                <SelectItem key={status.id} value={status.id}>
                                  {status.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={() => {
                            if (!newTransitionFromId || !newTransitionToId) {
                              showError('Selecciona origen y destino');
                              return;
                            }
                            if (newTransitionFromId === newTransitionToId) {
                              showError('El estado de origen y destino no pueden ser iguales');
                              return;
                            }
                            const existing = currentWorkflow.transitions.find(
                              (t) => t.fromStatusId === newTransitionFromId && t.toStatusId === newTransitionToId
                            );
                            if (existing) {
                              showError('Esta transición ya existe');
                              return;
                            }
                            createTransitionMutation.mutate(
                              {
                                workflowId: currentWorkflow.workflow.id,
                                fromStatusId: newTransitionFromId,
                                toStatusId: newTransitionToId,
                              },
                              {
                                onSuccess: () => {
                                  setNewTransitionFromId(undefined);
                                  setNewTransitionToId(undefined);
                                },
                              }
                            );
                          }}
                          disabled={createTransitionMutation.isPending}
                        >
                          Crear
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Transiciones</CardTitle>
                    <CardDescription>
                      {workflowType === 'booking'
                        ? `Citas · ${BOOKING_TYPE_INFO[selectedType].name}`
                        : `Trabajos · ${BOOKING_TYPE_INFO[selectedType].name}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {currentWorkflow.transitions.map((transition) => {
                          const fromStatus = getStatusById(transition.fromStatusId);
                          const toStatus = getStatusById(transition.toStatusId);
                          if (!fromStatus || !toStatus) return null;

                          return (
                            <TransitionRow
                              key={transition.id}
                              transition={transition}
                              fromStatus={fromStatus}
                              toStatus={toStatus}
                              onEdit={() => setEditingTransition(transition)}
                              onToggle={() =>
                                updateTransitionMutation.mutate({
                                  workflowId: currentWorkflow.workflow.id,
                                  transitionId: transition.id,
                                  updates: { isEnabled: !transition.isEnabled },
                                })
                              }
                              isUpdating={updateTransitionMutation.isPending}
                            />
                          );
                        })}
                      {currentWorkflow.transitions.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          No hay transiciones configuradas
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
          ) : (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-muted-foreground">
                  No hay flujo configurado para {workflowType === 'booking'
                    ? BOOKING_TYPE_INFO[selectedType].name
                    : `Trabajos · ${BOOKING_TYPE_INFO[selectedType].name}`}
                </div>
                {workflowType === 'job' && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      size="sm"
                      onClick={() => {
                        const baseWorkflow = workflows.find(
                          (w) => w.workflow.workflowType === 'job' && w.workflow.bookingType === null
                        ) || workflows.find((w) => w.workflow.workflowType === 'job');
                        if (!baseWorkflow) {
                          showError('No hay un flujo base de trabajos');
                          return;
                        }
                        cloneWorkflowMutation.mutate({
                          sourceWorkflowId: baseWorkflow.workflow.id,
                          bookingType: selectedType,
                        });
                      }}
                      disabled={cloneWorkflowMutation.isPending}
                    >
                      {cloneWorkflowMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      Crear flujo de trabajos
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Status Dialog */}
      <StatusEditDialog
        status={editingStatus}
        workflowType={workflowType}
        open={!!editingStatus}
        onOpenChange={(open) => !open && setEditingStatus(null)}
        onSave={(updates) => {
          if (!editingStatus || !currentWorkflow) return;
          updateStatusMutation.mutate(
            {
              workflowId: currentWorkflow.workflow.id,
              statusId: editingStatus.id,
              updates,
            },
            {
              onSuccess: () => setEditingStatus(null),
            }
          );
        }}
        isSaving={updateStatusMutation.isPending}
      />

      {currentWorkflow && (
        <StatusCreateDialog
          open={isCreateStatusOpen}
          onOpenChange={setIsCreateStatusOpen}
          workflowType={workflowType}
          onCreate={(data) =>
            createStatusMutation.mutate(
              {
                workflowId: currentWorkflow.workflow.id,
                data,
              },
              {
                onSuccess: () => setIsCreateStatusOpen(false),
              }
            )
          }
          isSaving={createStatusMutation.isPending}
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteWithFallback(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar estado</DialogTitle>
            <DialogDescription>
              Esta acción elimina el estado y sus transiciones.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Si hay trabajos o citas activas en este estado, se bloqueará la eliminación
              salvo que selecciones el fallback.
            </div>

            <div className="flex items-start gap-2">
              <Switch
                checked={deleteWithFallback}
                onCheckedChange={setDeleteWithFallback}
              />
              <div className="text-sm">
                <p className="font-medium">Mover a “Sin definir”</p>
                <p className="text-muted-foreground">
                  Reasigna los registros activos al estado de fallback antes de eliminar.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!deleteTarget || !currentWorkflow) return;
                deleteStatusMutation.mutate(
                  {
                    workflowId: currentWorkflow.workflow.id,
                    statusId: deleteTarget.id,
                    fallbackToUndefined: deleteWithFallback,
                  },
                  {
                    onSuccess: () => {
                      setDeleteTarget(null);
                      setDeleteWithFallback(false);
                    },
                  }
                );
              }}
              disabled={deleteStatusMutation.isPending}
            >
              {deleteStatusMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transition Dialog */}
      {currentWorkflow && (
        <TransitionEditDialog
          transition={editingTransition}
          fromStatus={editingTransition ? getStatusById(editingTransition.fromStatusId) : undefined}
          toStatus={editingTransition ? getStatusById(editingTransition.toStatusId) : undefined}
          preconditionDefinitions={preconditionDefs}
          postactionDefinitions={postactionDefs}
          emailTemplates={emailTemplates}
          checklistItems={checklistItems}
          onCreateChecklistItem={(data) => {
            if (!currentWorkflow) return;
            createChecklistItemMutation.mutate({
              workflowId: currentWorkflow.workflow.id,
              data,
            });
          }}
          onUpdateChecklistItem={(itemId, updates) => {
            if (!currentWorkflow) return;
            updateChecklistItemMutation.mutate({
              workflowId: currentWorkflow.workflow.id,
              itemId,
              updates,
            });
          }}
          onDeleteChecklistItem={(itemId) => {
            if (!currentWorkflow) return;
            deleteChecklistItemMutation.mutate({
              workflowId: currentWorkflow.workflow.id,
              itemId,
            });
          }}
          isSavingChecklist={
            createChecklistItemMutation.isPending ||
            updateChecklistItemMutation.isPending ||
            deleteChecklistItemMutation.isPending
          }
          open={!!editingTransition}
          onOpenChange={(open) => !open && setEditingTransition(null)}
          onSave={(updates) => {
            if (!editingTransition) return;
            updateTransitionMutation.mutate(
              {
                workflowId: currentWorkflow.workflow.id,
                transitionId: editingTransition.id,
                updates,
              },
              {
                onSuccess: () => setEditingTransition(null),
              }
            );
          }}
          isSaving={updateTransitionMutation.isPending}
        />
      )}
    </div>
  );
}
